import * as React from 'react';
import { useSelector } from 'react-redux';
import produce from 'immer';
import moment from 'moment';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import differenceBy from 'lodash/differenceBy';

import { navigate } from 'libs/router';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { Form, FormActions } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import TitleWithBackTo from 'components/title-with-back-to/TitleWithBackTo';

import ProfileEditControl from './components/profile-edit-control/ProfileEditControl';
import ProfileBanner from './components/profile-banner/ProfileBanner';
import ProfileMainInformation from './modules/main-information/ProfileMainInformation';
import ProfileSocialChannels from './modules/channels/ProfileSocialChannels';
import ProfileSpecialization from './modules/specialization/ProfileContentSpecialization';
import ProfileAbout from './modules/about/ProfileAbout';
import ProfileFashionIdentity from './modules/fashion-identity/ProfileFashionIdentity';
import ProfileSelfIdentity from './modules/self-identity/ProfileSelfIdentity';

import { danger } from 'redux-store/reducers/common-reducer';
import { validateChannels } from 'modules/channels/reducers/channels';
import { saveInfluencerProfile } from 'modules/influencers/reducers/influencerProfile';

import { useThunkDispatch } from 'hooks/use-thunk-dispatch';

import { __t, applyTranslations } from 'utils/language';
import { addProtocol } from 'utils/url';
import { hasFashionIdentity, hasSelfIdentity } from './utils';

import './influencer-edit-profile.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./influencer-edit-profile.i18n.json'));

import type {
  ChannelsValidationResult,
  InfluencerProfileForm,
  InfluencerSpecializedIn,
  State
} from 'types';
import type {
  ChannelSwitchExpiredAnalytics
} from 'modules/influencers/components/channels/channel-switch/types';

interface Props {
  profile: InfluencerProfileForm,
  isAdmin: boolean,
  expiredAnalyticsDataForInfluencer: ChannelSwitchExpiredAnalytics
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'influencer_edit_profile';

const InfluencerEditProfile: React.FC<Props> = ({
  profile,
  isAdmin,
  expiredAnalyticsDataForInfluencer
}) => {
  const [updatedProfile, setUpdatedProfile] = React.useState({} as InfluencerProfileForm);
  const [isModified, setIsModified] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [showBanner, setShowBanner] = React.useState(false);
  const dispatch = useThunkDispatch();
  const includesFashionIdentity = hasFashionIdentity(updatedProfile);
  const includesSelfIdentity = hasSelfIdentity(updatedProfile, 'edit');
  const backToUrl = `/content-creator/profiles/${updatedProfile.id}`;

  const scpPrepPartOneHideNewDate = useSelector((state: State) => (
    state.runtimeConfig.config.scpPrepProfilePartOneHideNewDate
  ));

  // add 1 day so hiding doesn't happen until the following day
  const hideDate = moment(scpPrepPartOneHideNewDate).utc().add(1, 'days');
  const shouldHideNew = moment().utc().isAfter(hideDate);

  const handleChange = (
    updates: Partial<InfluencerProfileForm>
  ): void => {
    setIsModified(true);
    setUpdatedProfile({
      ...updatedProfile,
      ...updates
    });
  };

  const submit = (validations: ChannelsValidationResult): void => {
    const profileId = updatedProfile.id;

    if (isNil(profileId)) {
      setHasError(true);
      dispatch(danger(__t(`${MSG_KEY}.errors.profile_id_missing`)));
      return;
    }

    const filteredProfile = produce(updatedProfile, (draftState) => {
      // eslint-disable-next-line no-param-reassign
      draftState.channels = validations.channels.filter(channel => channel.url);
      // eslint-disable-next-line no-param-reassign
      draftState.instagramExternalId = validations.instagramExternalId;
    });

    dispatch(saveInfluencerProfile(profileId, filteredProfile))
      .then(() => {
        setIsModified(false);
        navigate.to(`/content-creator/profiles/${profileId}`);
      })
      .catch(() => window.scrollTo(0, 0));
  };

  const handleErrors = (): void => {
    const specializedIn: InfluencerSpecializedIn = updatedProfile
      ? updatedProfile.specializedIn
      : {};

    const message = specializedIn.custom && specializedIn.custom.length > 3
      ? __t(`${MSG_KEY}.errors.too_many_items`)
      : __t(`${MSG_KEY}.errors.missing_fields`);

    setHasError(true);
    dispatch(danger(message));
  };

  const handleSubmit = (): void => {
    setIsModified(false);
    setHasError(false);

    const profileChannels = (profile.channels || []);
    const prevChannelUrls = profileChannels.map(c => c.url);
    const updatedChannels = (updatedProfile.channels || [])
      .filter(c => !prevChannelUrls.includes(c.url))
      .map(c => ({
        ...c,
        url: addProtocol(c.url, 'https')
      }));
    const channelsDiff = differenceBy(updatedProfile.channels ?? [], updatedChannels, 'url');

    dispatch(validateChannels(updatedChannels))
      .then((result: ChannelsValidationResult) => ({
        channels: [...result.channels, ...channelsDiff],
        instagramExternalId: result.instagramExternalId
      }))
      .then((result: ChannelsValidationResult) => submit(result))
      .catch((result: ChannelsValidationResult) => {
        const mergedResult: ChannelsValidationResult = {
          channels: [...result.channels, ...channelsDiff],
          instagramExternalId: result.instagramExternalId
        };
        // filter channels that were NOT validated
        const filteredChannels = profileChannels.filter(channel => {
          const foundValidatedChannel = mergedResult.channels.find(c => c.type === channel.type);
          return isNil(foundValidatedChannel);
        });

        setUpdatedProfile({
          ...updatedProfile,
          channels: [
            ...filteredChannels,
            ...mergedResult.channels
          ],
          instagramExternalId: result.instagramExternalId
        });

        handleErrors();
      });
  };

  const renderButtons = (
    type: 'page' | 'control'
  ): React.ReactNode => {
    const buttonClasses = classnames('influencer-edit-profile__buttons', {
      [`influencer-edit-profile__buttons--type-${type}`]: Boolean(type)
    });

    return (
      <div className={buttonClasses}>
        <ActionButton
          color="secondary"
          className="influencer-edit-profile__button-cancel"
          link={backToUrl}
        >
          {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
        </ActionButton>
        <ActionButton
          type="submit"
          color="primary"
          className="influencer-edit-profile__button-save"
        >
          {__t(`${COMMON_MSG_KEY}.action_button.save_changes`)}
        </ActionButton>
      </div>
    );
  };

  React.useEffect(() => {
    if (isEmpty(updatedProfile)) {
      setUpdatedProfile(profile);
    } else {
      setShowBanner(!shouldHideNew && (!includesFashionIdentity || !includesSelfIdentity));
    }
  });

  return (
    <div className="influencer-edit-profile">
      {
        isModified && (
          <PromptExit
            type="page"
            title={__t(`${MSG_KEY}.modal.title`)}
            message={__t(`${MSG_KEY}.modal.message`)}
            actionButtonLabel={__t(`${MSG_KEY}.modal.buttons.confirm`)}
            cancelButtonLabel={__t(`${MSG_KEY}.modal.buttons.cancel`)}
          />
        )
      }
      <Form
        className="influencer-edit-profile__form"
        onSubmit={handleSubmit}
        onValidationErrors={handleErrors}
      >
        <TitleWithBackTo
          title={__t(`${MSG_KEY}.title`)}
          backToUrl={backToUrl}
          backToHoverText={__t(`${MSG_KEY}.back_to`)}
          withBold
        />
        <ProfileEditControl
          renderButtons={renderButtons}
        />
        {
          showBanner && (
            <ProfileBanner
              anchors={[
                'profile-fashion-identity',
                'profile-self-identity'
              ]}
              includesFashionIdentity={includesFashionIdentity}
              includesSelfIdentity={includesSelfIdentity}
            />
          )
        }
        <ProfileMainInformation
          profile={updatedProfile}
          hasError={hasError}
          onChange={handleChange}
        />
        <ProfileSocialChannels
          profile={updatedProfile}
          isAdmin={isAdmin}
          hasError={hasError}
          expiredAnalyticsDataForInfluencer={expiredAnalyticsDataForInfluencer}
          onChange={handleChange}
        />
        <ProfileSpecialization
          profile={updatedProfile}
          onChange={handleChange}
        />
        <ProfileAbout
          profile={updatedProfile}
          onChange={handleChange}
        />
        <ProfileFashionIdentity
          profile={updatedProfile}
          hideNew={shouldHideNew}
          onChange={handleChange}
        />
        <ProfileSelfIdentity
          profile={updatedProfile}
          hideNew={shouldHideNew}
          onChange={handleChange}
        />
        <FormActions align="center">
          {renderButtons('page')}
        </FormActions>
      </Form>
    </div>
  );
};

export default InfluencerEditProfile;
